/* eslint-disable @typescript-eslint/no-explicit-any */
// url to your service

import { Dispatch, useContext, useMemo, useRef } from 'react'
import { Message } from 'google-protobuf'
import {
  USE_CASE,
  USER_ROLE,
} from '@trustero/trustero-api-web/lib/account/account_pb'
import { ToastPrompts } from 'src/Utils/helpers/toast'
import { toast } from 'react-toastify'
import log from 'loglevel'
import { getRpcAccess, RpcAccessDenied } from '../app/AppAuth/rpcAuthorization'
import AuthContext, {
  AuthAction,
  AuthActionType,
  AuthRecord,
} from '../context/authContext'
import ContentContext, { Content } from '../context/ContentContext'
import { GrpcCall } from '../components/async/utils'
import {
  useGrpcRevalidateByMethod,
  useGrpcRevalidateByMethodRequest,
} from '../components'
import {
  authHeader,
  NTRCE_API_URL,
  translateTokenError,
  attemptTokenRefresh,
} from './gRpcAdapter'
import { callApiAndUpdateContent } from './dataModelAdapterUtils'
import { getRefererHeader } from './referer'
import { getSwrRequestIdHeader } from './requestIdHeader'
import {
  continuesSession,
  createRpcSession,
  CurrentAuth,
  useCurrentAuth,
} from './rpcSession'

export type GrpcClientCons<T> = {
  new (arg1: string, arg2: any, arg3: any): T
}

export type GrpcClient<T> = T & {
  header: { Authorization?: string }
  accountId?: string
}

export const authorizedGrpcClientFactory = <T>(
  clientCons: GrpcClientCons<T>,
  auth?: AuthRecord | null,
  authDispatch?: Dispatch<AuthAction> | null,
  currentAuth?: CurrentAuth,
): GrpcClient<T> => {
  const client = new clientCons(NTRCE_API_URL, null, null)

  const isCurrent = () => !currentAuth || continuesSession(auth, currentAuth())
  // A client saved before a same-scope token refresh sends the current token.
  const sessionAuth = () => currentAuth?.() ?? auth
  const header = {
    ...authHeader(auth?.accessToken),
    ...getRefererHeader(),
  }
  for (const prop in client) {
    if (typeof client[prop] !== 'function') {
      continue
    }

    // eslint-disable-next-line @typescript-eslint/ban-types
    const original = client[prop] as unknown as Function
    client[prop] = ((...args: any[]) => {
      if (!isCurrent()) return rejectRpc(original)
      const live = sessionAuth()
      if (getRpcAccess(original, live, args[0]) !== 'allowed') {
        return rejectRpc(original)
      }
      const session = createRpcSession(live, currentAuth, original, args[0])
      const metadata = {
        ...args[1],
        ...authHeader(live?.accessToken),
        ...getRefererHeader(),
      }
      args[1] = {
        ...metadata,
        ...getSwrRequestIdHeader(metadata),
      }

      return original
        .call(client, ...args)
        .then((response: unknown) => {
          if (!session.isCurrent()) throw new RpcAccessDenied(original)
          return response
        })
        .catch(async (err: Error & { code?: number }) => {
          if (!session.isCurrent() && !session.canRecover(err))
            throw new RpcAccessDenied(original)
          if (err instanceof RpcAccessDenied) throw err
          if (
            err?.code === 7 &&
            err.message.includes('this account is read only')
          ) {
            toast(ToastPrompts.READ_ONLY_ACCOUNT)
          }

          const parsedErrors:
            | {
                token: any
                general: any
                void?: boolean
                shouldRefresh?: boolean
              }
            | {
                token: any
                void?: boolean
                shouldRefresh?: boolean
              } = translateTokenError(err)

          log.debug(
            'authorizedGrpcClientFactory: error code:',
            err?.code,
            'parsedErrors:',
            parsedErrors,
          )

          if (parsedErrors.token) {
            let logoutToken = live?.accessToken
            // If the error is due to token/permission version mismatch, try refreshing first
            if (parsedErrors.shouldRefresh) {
              log.info(
                'Token/permission version error detected, attempting refresh',
              )
              const newToken = await attemptTokenRefresh(live, authDispatch)
              if (newToken) {
                await session.acceptRefresh(newToken)
                logoutToken = newToken
                log.info('Retrying original request with refreshed token')
                // Retry the original request with the new token
                try {
                  if (!session.isCurrent()) throw new RpcAccessDenied(original)
                  const newHeader = {
                    ...authHeader(newToken),
                    ...getRefererHeader(),
                  }
                  const metadata = {
                    ...args[1],
                    ...newHeader,
                  }
                  args[1] = {
                    ...metadata,
                    ...getSwrRequestIdHeader(metadata),
                  }
                  const response = await original.call(client, ...args)
                  if (!session.isCurrent()) throw new RpcAccessDenied(original)
                  return response
                } catch (retryErr) {
                  if (!session.isCurrent()) throw new RpcAccessDenied(original)
                  if (retryErr instanceof RpcAccessDenied) throw retryErr
                  const retryErrors = translateTokenError(retryErr)
                  if (!retryErrors.token || retryErrors.shouldRefresh)
                    throw retryErr
                }
              } else {
                log.warn('Token refresh failed, will logout')
              }
            }
            // If refresh didn't work or wasn't applicable, logout
            if (!session.isCurrent()) throw new RpcAccessDenied(original)
            log.info('Logging out due to token error')
            authDispatch &&
              authDispatch({
                type: AuthActionType.LOGOUT,
                sourceAccessToken: logoutToken,
                sourceAccountId: live?.accountId,
                useCase: USE_CASE.SAAS_BUYER,
                role: USER_ROLE.READONLY,
                logoutReason: 'grpc_token_error',
              })
            return
          }

          if (parsedErrors.void === true) {
            return
          }

          throw err
        })
    }) as any
  }
  return {
    ...client,
    header,
    accountId: auth?.accountId,
  }
}

export function authorizedGrpcClientFactoryWithContentUpdate<T>(
  clientCons: GrpcClientCons<T>,
  grpcUrl: string,
  auth: AuthRecord,
  authDispatch: Dispatch<AuthAction>,
  content: Content,
  contentUpdate: Dispatch<Content>,
  methodMutator: (asyncCall: unknown) => Promise<unknown>,
  methodRequestMutator: (
    asyncCall: GrpcCall<Message, Message>,
    request: Message,
  ) => Promise<Message>,
  currentAuth?: CurrentAuth,
  currentContent?: () => Content,
): GrpcClient<T> {
  const client = new clientCons(grpcUrl, null, null)
  const isCurrent = () => !currentAuth || continuesSession(auth, currentAuth())
  // A client saved before a same-scope token refresh sends the current token.
  const sessionAuth = () => currentAuth?.() ?? auth

  const header = authHeader(auth.accessToken)
  for (const prop in client) {
    if (typeof client[prop] !== 'function') {
      continue
    }

    // eslint-disable-next-line @typescript-eslint/ban-types
    const original = client[prop] as unknown as Function
    client[prop] = ((...args: any[]) => {
      if (!isCurrent()) return rejectRpc(original)
      const live = sessionAuth()
      if (getRpcAccess(original, live, args[0]) !== 'allowed')
        return rejectRpc(original)
      const session = createRpcSession(live, currentAuth, original, args[0])
      const update: Dispatch<Content> = (next) => {
        if (session.isCurrent()) contentUpdate(next)
      }
      let rpcCompleted = false
      const invoke = async () => {
        if (!session.isCurrent()) throw new RpcAccessDenied(original)
        const response = await original.call(client, ...args)
        rpcCompleted = true
        if (!session.isCurrent()) throw new RpcAccessDenied(original)
        return response
      }
      const callWithContent = async () => {
        const response = await callApiAndUpdateContent(
          invoke,
          currentContent?.() ?? content,
          update,
          () => session.auth as AuthRecord,
          authDispatch,
          (method) => {
            if (!session.isCurrent()) throw new RpcAccessDenied(original)
            return methodMutator(method)
          },
          (method, request) => {
            if (!session.isCurrent()) throw new RpcAccessDenied(original)
            return methodRequestMutator(method, request)
          },
        )
        if (!session.isCurrent()) throw new RpcAccessDenied(original)
        return response
      }
      const metadata = {
        ...args[1],
        ...authHeader(live.accessToken),
      }
      args[1] = {
        ...metadata,
        ...getSwrRequestIdHeader(metadata),
      }

      return callWithContent().catch(async (err: Error & { code?: number }) => {
        // A failed follow-up cache refresh must never replay a completed write.
        if (rpcCompleted) throw err
        if (!session.isCurrent() && !session.canRecover(err))
          throw new RpcAccessDenied(original)
        if (err instanceof RpcAccessDenied) throw err
        const parsedErrors:
          | {
              token: any
              general: any
              void?: boolean
              shouldRefresh?: boolean
            }
          | {
              token: any
              void?: boolean
              shouldRefresh?: boolean
            } = translateTokenError(err)

        log.debug(
          'authorizedGrpcClientFactoryWithContentUpdate: error code:',
          err?.code,
          'parsedErrors:',
          parsedErrors,
        )

        if (parsedErrors.token) {
          let logoutToken = live.accessToken
          // If the error is due to token/permission version mismatch, try refreshing first
          if (parsedErrors.shouldRefresh) {
            log.info(
              'Token/permission version error detected, attempting refresh',
            )
            const newToken = await attemptTokenRefresh(live, authDispatch)
            if (newToken) {
              await session.acceptRefresh(newToken)
              logoutToken = newToken
              log.info('Retrying original request with refreshed token')
              // Retry the original request with the new token
              try {
                const newHeader = authHeader(newToken)
                const metadata = {
                  ...args[1],
                  ...newHeader,
                }
                args[1] = {
                  ...metadata,
                  ...getSwrRequestIdHeader(metadata),
                }
                return await callWithContent()
              } catch (retryErr) {
                if (rpcCompleted) throw retryErr
                if (!session.isCurrent()) throw new RpcAccessDenied(original)
                if (retryErr instanceof RpcAccessDenied) throw retryErr
                const retryErrors = translateTokenError(retryErr)
                if (!retryErrors.token || retryErrors.shouldRefresh)
                  throw retryErr
              }
            } else {
              log.warn('Token refresh failed, will logout')
            }
          }
          // If refresh didn't work or wasn't applicable, logout
          if (!session.isCurrent()) throw new RpcAccessDenied(original)
          log.info('Logging out due to token error')
          authDispatch({
            type: AuthActionType.LOGOUT,
            sourceAccessToken: logoutToken,
            sourceAccountId: live.accountId,
            useCase: USE_CASE.SAAS_BUYER,
            role: USER_ROLE.READONLY,
            logoutReason: 'grpc_token_error_with_content_update',
          })
        }

        if (parsedErrors.void === true) {
          return
        }

        throw err
      })
    }) as any
  }
  return {
    ...client,
    header,
    accountId: auth?.accountId,
  }
}

export function useAuthorizedGrpcClient<T>(
  client: GrpcClientCons<T>,
): GrpcClient<T> {
  const { authCtx, authDispatch } = useContext(AuthContext)
  const currentAuth = useCurrentAuth(authCtx)
  return useMemo(
    () =>
      authorizedGrpcClientFactory(client, authCtx, authDispatch, currentAuth),
    [authCtx, authDispatch, client, currentAuth],
  )
}

export function useAuthorizedGrpcClientWithContentUpdate<T>(
  client: GrpcClientCons<T>,
): GrpcClient<T> {
  const { authCtx, authDispatch } = useContext(AuthContext)
  const currentAuth = useCurrentAuth(authCtx)
  const { content, contentDispatch } = useContext(ContentContext)
  const methodMutator = useGrpcRevalidateByMethod()
  const methodRequestMutator = useGrpcRevalidateByMethodRequest()
  const latest = useRef({ content, methodMutator, methodRequestMutator })
  latest.current = { content, methodMutator, methodRequestMutator }

  return useMemo(
    () =>
      authorizedGrpcClientFactoryWithContentUpdate(
        client,
        NTRCE_API_URL,
        authCtx,
        authDispatch,
        content,
        contentDispatch,
        (method) => latest.current.methodMutator(method),
        (method, request) =>
          latest.current.methodRequestMutator(method, request),
        currentAuth,
        () => latest.current.content,
      ),
    [client, authCtx, authDispatch, content, contentDispatch, currentAuth],
  )
}

function rejectRpc(method: unknown) {
  return Promise.reject(new RpcAccessDenied(method))
}
